import React, { useLayoutEffect, useRef } from 'react';

/*
  Time per pixel, not a duration: a fixed time makes a name that overruns by a word whip past and
  one that overruns by a line crawl, because the same clock covers different distances.
*/
const SLIDE_SPEED_MS = 8;

export type SlidingTextProps = {
  children: React.ReactNode;
  name?: string;
};

/**
 * Text too long for the box it is in: clipped, then slid back by exactly its own overflow so the
 * tail can be read without the box ever changing size.
 *
 * **It takes its width from its parent rather than giving it one**, so the parent needs a width of
 * its own. Hovering the text reveals it; anything that wants to reveal it on its own terms - a row
 * hovered anywhere along its length, a menu left open - carries `revealed` instead.
 */
export const SlidingText = ({ children, name }: SlidingTextProps) => {
  const boxRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLSpanElement>(null);

  /*
    **How long the travel should take.** The travel itself is CSS's - `100cqw - 100%` - but no
    expression turns a length into a time, so the distance is read here and priced at the rate
    above.
  */
  const measure = () => {
    const box = boxRef.current;
    const content = contentRef.current;

    if (!box || !content) return;

    const distance = Math.max(0, content.scrollWidth - box.clientWidth);

    content.style.transitionDuration = `${distance * SLIDE_SPEED_MS}ms`;
  };

  /*
    Measured before the first paint so a reveal driven by `revealed` - a parent's doing, which this
    hears nothing about - travels at the same rate, and again as the pointer arrives because the
    column it sits in can be dragged narrower in between.
  */
  useLayoutEffect(measure, [children]);

  return (
    <span
      className="sliding-text"
      data-test={name}
      onPointerEnter={measure}
      ref={boxRef}
    >
      <span
        className="sliding-text-content"
        data-test={name ? `${name}_CONTENT` : null}
        ref={contentRef}
      >
        {children}
      </span>
    </span>
  );
};
